import React, { useState, useRef, useEffect } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { formatCurrency, creditSourceLabel } from '../../utils/formatters';

// creditapply-4d8b1e63 -- SEAM 4a client half: apply an existing open account
// credit to THIS invoice (manual selection only). The server callable
// applyCreditToInvoice enforces every ceiling (invoice balance, credit balance,
// one-application-per-credit/invoice); this modal is the operator surface. A
// credit already applied to this invoice is shown DISABLED with an explanation
// (its remaining balance can go to a different invoice) -- never hidden.

const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// Client-side balance derivation mirroring InvoiceDetailsView's Variant-A idiom
// (amountPaid ?? sum(paymentHistory); balanceDue else total - amountPaid; sub-cent
// clamp). The server's computeInvoiceBalance is the real ceiling; this only drives
// the default/max in the form.
const deriveBalanceDue = (invoice) => {
    const total = Number(invoice?.total) || 0;
    const history = Array.isArray(invoice?.paymentHistory) ? invoice.paymentHistory : [];
    const amountPaid = invoice?.amountPaid ?? history.reduce((s, p) => s + (Number(p && p.amount) || 0), 0);
    let balanceDue = invoice?.balanceDue !== undefined ? Number(invoice.balanceDue) : (total - amountPaid);
    if (balanceDue < 0.01) balanceDue = 0;
    return round2(balanceDue);
};

const ApplyCreditModal = ({ isOpen, onClose, invoice, currentUserProfile, onSuccess }) => {
    const [credits, setCredits] = useState([]);
    const [loadingCredits, setLoadingCredits] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [selectedCreditId, setSelectedCreditId] = useState('');
    const [amount, setAmount] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(false);

    // Synchronous double-click guard: a ref write is immediate where React state
    // lags a rapid second click. (This is the CLIENT guard; the server's
    // deterministic entry id is the real idempotency backstop.)
    const submittingRef = useRef(false);

    const orgId = currentUserProfile?.organizationId;
    const invoiceBalanceDue = deriveBalanceDue(invoice);

    // creditunapply-2f6b8d41 -- Credit ids with a LIVE application on THIS invoice: an
    // isCredit paymentHistory row whose creditEntryId has NOT been reversed by a later
    // isCreditReversal row. A since-unapplied credit drops out of this set, so it becomes
    // selectable again -- the UI half of the server's applicationSeq re-apply support.
    // Used to render still-applied credits disabled-with-reason.
    const ph = Array.isArray(invoice?.paymentHistory) ? invoice.paymentHistory : [];
    const reversedEntryIds = new Set(
        ph.filter((p) => p && p.isCreditReversal && p.reversedCreditEntryId)
            .map((p) => p.reversedCreditEntryId)
    );
    const appliedHereIds = new Set(
        ph.filter((p) => p && p.isCredit && p.creditId && !reversedEntryIds.has(p.creditEntryId))
            .map((p) => p.creditId)
    );

    // Load the customer's credits once when the modal opens.
    useEffect(() => {
        if (!isOpen || !orgId || !invoice?.customerId) return;
        let cancelled = false;
        setLoadingCredits(true);
        setLoadError(null);
        (async () => {
            try {
                const qs = await getDocs(query(
                    collection(db, `organizations/${orgId}/customerCredits`),
                    where('customerId', '==', invoice.customerId)
                ));
                if (cancelled) return;
                setCredits(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (err) {
                if (cancelled) return;
                console.error(err);
                setLoadError('Could not load account credits. Please try again.');
            } finally {
                if (!cancelled) setLoadingCredits(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, orgId, invoice?.customerId]);

    if (!isOpen) return null;

    // Selectable: open, has a positive balance, and not already applied to this
    // invoice. Applied-here: shown disabled with an explanation (superset per §9).
    const selectableCredits = credits.filter(
        (c) => c.status === 'open' && round2(c.availableBalance) > 0 && !appliedHereIds.has(c.id)
    );
    const appliedHereCredits = credits.filter((c) => appliedHereIds.has(c.id));

    const selectedCredit = selectableCredits.find((c) => c.id === selectedCreditId) || null;
    const maxApplicable = selectedCredit
        ? round2(Math.min(round2(selectedCredit.availableBalance), invoiceBalanceDue))
        : 0;

    const handleSelectCredit = (creditId) => {
        setSelectedCreditId(creditId);
        setFormError(null);
        const c = selectableCredits.find((x) => x.id === creditId);
        if (c) {
            const def = round2(Math.min(round2(c.availableBalance), invoiceBalanceDue));
            setAmount(def > 0 ? def.toFixed(2) : '');
        } else {
            setAmount('');
        }
    };

    const handleClose = () => {
        if (submittingRef.current) return; // never close mid-flight
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return; // reject a rapid second click
        if (!selectedCredit) {
            setFormError('Select a credit to apply.');
            return;
        }
        const amountNum = round2(amount);
        if (!Number.isFinite(amountNum) || amountNum <= 0) {
            setFormError('Enter an amount greater than zero.');
            return;
        }
        if (amountNum > maxApplicable + 0.01) {
            setFormError(`The most you can apply here is ${formatCurrency(maxApplicable)}.`);
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setFormError(null);
        try {
            const fn = httpsCallable(functions, 'applyCreditToInvoice');
            await fn({ creditId: selectedCredit.id, invoiceId: invoice.id, amount: amountNum });
            setFormSuccess(true);
            setTimeout(() => {
                if (onSuccess) onSuccess();
                onClose();
            }, 1200);
        } catch (error) {
            console.error(error);
            // HttpsError.message (e.g. the server's ceiling / one-per-pair conflict
            // message) reaches the operator verbatim.
            setFormError(error?.message || 'Failed to apply the credit. Please try again.');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const amountNum = round2(amount);
    const creditRemainingAfter = selectedCredit ? round2(round2(selectedCredit.availableBalance) - amountNum) : 0;
    const invoiceBalanceAfter = round2(invoiceBalanceDue - amountNum);
    const previewValid = selectedCredit && Number.isFinite(amountNum) && amountNum > 0 && amountNum <= maxApplicable + 0.01;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800">Apply Account Credit</h2>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        Apply an existing account credit to this invoice. Balance due:{' '}
                        <span className="font-medium text-gray-800">{formatCurrency(invoiceBalanceDue)}</span>.
                    </p>

                    {loadingCredits ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                            <Loader2 className="animate-spin" size={16} /> Loading credits…
                        </div>
                    ) : loadError ? (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <AlertTriangle size={16} className="shrink-0" />
                            {loadError}
                        </div>
                    ) : (selectableCredits.length === 0 && appliedHereCredits.length === 0) ? (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm">
                            This customer has no account credits available to apply.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Choose a credit</label>
                            {selectableCredits.map((c) => (
                                <label
                                    key={c.id}
                                    className={`flex items-center justify-between gap-3 p-3 border rounded-lg cursor-pointer ${selectedCreditId === c.id ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:bg-gray-50'}`}
                                >
                                    <span className="flex items-center gap-3 min-w-0">
                                        <input
                                            type="radio"
                                            name="creditId"
                                            value={c.id}
                                            checked={selectedCreditId === c.id}
                                            onChange={() => handleSelectCredit(c.id)}
                                        />
                                        <span className="min-w-0">
                                            <span className="block text-sm text-gray-700 truncate">{creditSourceLabel(c)}</span>
                                            {c.memo && (
                                                <span className="block text-xs text-gray-500 truncate">Ref: {c.memo}</span>
                                            )}
                                        </span>
                                    </span>
                                    <span className="text-sm font-medium text-gray-900 shrink-0">
                                        {formatCurrency(round2(c.availableBalance))}
                                    </span>
                                </label>
                            ))}
                            {appliedHereCredits.map((c) => {
                                const remaining = round2(c.availableBalance);
                                return (
                                    <div
                                        key={c.id}
                                        className="flex items-start justify-between gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50 opacity-70"
                                    >
                                        <span className="min-w-0">
                                            <span className="block text-sm text-gray-500 truncate">
                                                {creditSourceLabel(c)}{c.memo ? ` · Ref: ${c.memo}` : ''}
                                            </span>
                                            <span className="block text-xs text-gray-500 mt-0.5">
                                                {remaining > 0
                                                    ? `Already applied to this invoice — its remaining ${formatCurrency(remaining)} can be applied to a different invoice.`
                                                    : 'Fully applied to this invoice.'}
                                            </span>
                                        </span>
                                        <span className="text-sm font-medium text-gray-400 shrink-0">
                                            {formatCurrency(remaining)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {selectedCredit && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Amount to apply</label>
                                <div className="relative mt-1 rounded-md shadow-sm">
                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                        <span className="text-gray-500 sm:text-sm">$</span>
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={maxApplicable}
                                        value={amount}
                                        onChange={(e) => { setAmount(e.target.value); setFormError(null); }}
                                        className="block w-full rounded-md border-gray-300 pl-7 py-2 border focus:ring-purple-500 focus:border-purple-500"
                                        required
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Up to {formatCurrency(maxApplicable)} (the smaller of the credit's balance and the invoice balance).
                                </p>
                            </div>

                            {previewValid && (
                                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-900">
                                    Applying <span className="font-semibold">{formatCurrency(amountNum)}</span> from this credit.
                                    <div className="mt-1 text-purple-800">
                                        Credit remaining after: <span className="font-medium">{formatCurrency(creditRemainingAfter)}</span>
                                        {' · '}
                                        Invoice balance after: <span className="font-medium">{formatCurrency(invoiceBalanceAfter)}</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <button
                        type="submit"
                        disabled={isSubmitting || formSuccess || !selectedCredit}
                        className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 flex justify-center items-center gap-2"
                    >
                        {isSubmitting ? <Loader2 className="animate-spin" /> : 'Apply Credit'}
                    </button>

                    {formSuccess && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                            <CheckCircle size={16} className="shrink-0" />
                            {`Applied ${formatCurrency(amountNum)} to this invoice.`}
                        </div>
                    )}

                    {formError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <AlertTriangle size={16} className="shrink-0" />
                            {formError}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ApplyCreditModal;
